/* eslint-disable @typescript-eslint/naming-convention */
import { DateTimeUtils } from "@sazka/utils/date-time-utils";
import { WebpHelper } from "@sazka/utils/webp-helper";
import { LotteryParser } from "./lottery-parser";
import { Lottery } from "../models/lottery";

/**
 * This namespace provides utility functions for lottery-related operations and checks.
 */
export namespace LotteryUtils {
    export const extraGameLotteryNamesPairing = [
        [Lottery.sportka, Lottery.sportkaMh],
        [Lottery.extraRenta, Lottery.extraRentaMh],
        [Lottery.sance, Lottery.sanceMh],
    ];

    export const isExtraGame = (lotteryName: Lottery) =>
        extraGameLotteryNamesPairing.map((pair) => pair[1]).includes(lotteryName);

    export const getExtraGameNameForMainLottery = (mainLotteryName: string) =>
        extraGameLotteryNamesPairing.find((pair) => pair[0] == mainLotteryName)?.[1];

    export const getMainLotteryNameForExtraGame = (extraGameLotteryName: string) =>
        extraGameLotteryNamesPairing.find((pair) => pair[1] == extraGameLotteryName)?.[0];

    export const getMainLotteryNameForLottery = (lotteryName: string) =>
        getMainLotteryNameForExtraGame(lotteryName) ?? lotteryName;

    export const isSportka = (lottery: Lottery) => {
        return lottery === Lottery.sportka || lottery === Lottery.sportkaMh;
    };

    export const hasSystem = (game: Lottery) => {
        switch (game) {
            case Lottery.sportka:
            case Lottery.sportkaMh:
            case Lottery.eurojackpot:
            case Lottery.euromiliony:
                return true;
            default:
                return false;
        }
    };

    export const hasAddon = (game: Lottery) => {
        switch (game) {
            case Lottery.sportka:
            case Lottery.sportkaMh:
            case Lottery.stastnych10:
            case Lottery.eurojackpot:
            case Lottery.euromiliony:
                return true;
            default:
                return false;
        }
    };

    export const hasSyndicate = (lottery: Lottery) => {
        switch (lottery) {
            case Lottery.sportka:
            case Lottery.sportkaMh:
            case Lottery.eurojackpot:
                return true;
            default:
                return false;
        }
    };

    export const isMinuteLottery = (lottery: Lottery) => {
        switch (lottery) {
            case Lottery.vsechnoNeboNic:
            case Lottery.rychleKacky:
            case Lottery.powerSpin:
                return true;
            default:
                return false;
        }
    };

    export const isFiveMinuteLottery = (lottery: Lottery) => {
        switch (lottery) {
            case Lottery.keno:
            case Lottery.kameny:
            case Lottery.rychla6:
                return true;
            default:
                return false;
        }
    };

    export const isQuickLottery = (lottery: Lottery) => {
        return isMinuteLottery(lottery) || isFiveMinuteLottery(lottery);
    };

    export const generateRandomNumbers = (
        count: number,
        min: number,
        max: number,
        alreadyPickedNumbers?: number[],
        sort = false
    ) => {
        const resultArray: number[] = alreadyPickedNumbers ? [...alreadyPickedNumbers] : [];
        const countOfNewGenValues = count - resultArray.length;

        if (!!countOfNewGenValues) {
            const availableNumbers = Array.from({ length: max - min + 1 }, (_, i) => i + min).filter(
                (num) => !alreadyPickedNumbers?.includes(num)
            );

            for (let i = 0; i < countOfNewGenValues && availableNumbers.length > 0; i++) {
                const index = Math.floor(Math.random() * availableNumbers.length);
                resultArray.push(availableNumbers[index]);
                availableNumbers.splice(index, 1);
            }
        }
        return sort ? resultArray.sort((a, b) => a - b) : resultArray;
    };

    export const generateAddon = (game: Lottery) => {
        const maxNumbers = game === Lottery.euromiliony ? 5 : 6;
        return Array.from({ length: maxNumbers }, () => Math.floor(Math.random() * 10));
    };

    export const calculateMultidrawEndDate = (firstDrawDate: Date, duration: number, drawDays: number[]) => {
        // Helper function to find the next draw day from the given current day
        const findNextDrawDate = (currentDate: Date, dayOfWeek: number) => {
            const currentDay = currentDate.getDay(); // 0 (Sunday) to 6 (Saturday)
            let daysToAdd = dayOfWeek - currentDay;
            if (daysToAdd < 0) daysToAdd += 7; // Wrap around if needed
            const nextDate = new Date(currentDate);
            nextDate.setDate(currentDate.getDate() + daysToAdd);
            return nextDate;
        };

        drawDays = drawDays.sort((a, b) => a - b);

        const totalDays = drawDays.length;
        const fullWeeks = Math.floor((duration - 1) / totalDays);
        const remainder = (duration - 1) % totalDays;

        // Calculate last draw date
        let lastDrawDate = new Date(firstDrawDate);

        // Add full weeks
        lastDrawDate.setDate(lastDrawDate.getDate() + fullWeeks * 7);

        // Handle remainding days
        if (remainder > 0) {
            const lastDrawDay = lastDrawDate.getDay(); // Day of week (0-6)
            const currentIndex = drawDays.findIndex((day) => day === lastDrawDay);
            const nextIndex = (currentIndex + 1) % totalDays; // Circular index
            const nextDayOfWeek = drawDays[nextIndex];

            lastDrawDate = findNextDrawDate(lastDrawDate, nextDayOfWeek);
        }

        return lastDrawDate;
    };

    export const formatDrawDay = (date: Date) => {
        if (!date) return "";
        return DateTimeUtils.atWeekdayWithDate(date);
    };

    export const formatDrawDate = (date: Date, lottery: Lottery, drawId?: number) => {
        if (!date || !lottery) return "";
        return `${DateTimeUtils.atWeekdayWithDate(date)} ${LotteryParser.drawLabel(lottery, date, drawId)}`;
    };

    export const myTicketsLotteryLogo = (game: Lottery, size: "small" | "medium" | "big", cssClass?: string) => {
        return WebpHelper.createPictureTag(
            `/frontend/web/img/lotteries/my-tickets/lottery-img/${game}--${size}.jpg|/frontend/web/img/lotteries/my-tickets/lottery-img/${game}--${size}@2x.jpg`,
            `Logo - ${LotteryParser.lotteryDisplayName(game)}`,
            cssClass,
            142,
            113,
            false
        );
    };

    export const betOnlineUrlSyndicate = (lottery: Lottery) => {
        const lot = lottery === Lottery.sportkaMh ? Lottery.sportka : lottery;
        return `/loterie/skupinove-sazeni/${lot}`;
    };

    export const betOnlineUrl = (lottery: Lottery, isSystem = false, queryParameter = null) => {
        const hasCustomBetLayout =
            lottery === Lottery.powerSpin || lottery === Lottery.vsechnoNeboNic || lottery === Lottery.rychleKacky;
        let url = hasCustomBetLayout ? `/loterie/${lottery}/vsadit-si-online` : `/loterie/${lottery}/vsadit-si/sazenka`;

        if (lottery == Lottery.stastneDatum) {
            url += "/orloj";
        }

        if (isSystem && hasSystem(lottery)) {
            url += "/systemova";
        }

        if (queryParameter) {
            url += `?${queryParameter}`;
        }
        return url;
    };

    export const productPageUrl = (lottery: Lottery) => {
        return `/loterie/${lottery}/vsadit-si`;
    };

    export const checkOnlineUrl = (lottery: Lottery) => {
        const url = `/loterie/${lottery}/kontrola-a-vysledky`;
        return url;
    };

    export const resultsPageUrl = (lottery: Lottery) => {
        const url = `/loterie/${lottery}/vysledky`;
        return url;
    };
}
